<template>
  <!--
    Formulario de contacto guiado (Diseño "Guiado" del formulario de contacto, panel → Contacto / Inicio).
    Primero el motivo; después solo lo que la notaría necesita para responder a la primera.
    Si es un trámite que se puede hacer online, se ofrece el encargo online. Copias, facturas y "cómo va lo mío"
    ofrecen antes la Notaría Virtual (si la notaría la tiene), donde el cliente ya puede verlo.
  -->
  <div class="cg home-section" :class="{ 'form-en-plantilla': enPlantilla }" id="contact" :style="{ '--cg-acento': acento, '--cg-acento-texto': acentoTexto, '--cg-enlace': enlace, '--cg-texto': colorTexto }"
    :dir="IDIOMAS_RTL.includes(idiomaSolicitud) ? 'rtl' : 'ltr'">
    <v-container>
      <v-row class="cg__fila">
        <v-col :lg="conImagen ? 7 : 12" cols="12" :order="control.ImagenPosicion === 1 ? 2 : 1">
          <div class="cg__tarjeta">
            <div class="cg__cabecera" :style="{ textAlign: control.Titulo?.css?.Align }">
              <h2 class="cg__titulo" :style="tituloStyle" v-html="control.Titulo?.Texto"></h2>
              <p v-if="control.Descripcion?.Texto" class="cg__descripcion" :style="descripcionStyle" v-html="control.Descripcion.Texto"></p>
            </div>

            <ClientOnly>
              <div v-if="!textosListos" class="cg__cargando" />

              <!-- Enviado -->
              <div v-else-if="enviado" class="cg__ok" role="status">
                <v-icon size="44" color="success">mdi-check-circle-outline</v-icon>
                <!-- Trámite: la notaría lo tiene como consulta con referencia (pestaña "Presupuestos"); el cliente la sigue en su página.
                     Ref: https://trello.com/c/63NGWLHt -->
                <template v-if="referenciaEnvio">
                  <h3>{{ t('Hemos recibido su consulta') }}</h3>
                  <p class="cg__referencia">{{ t('Referencia') }} <strong>{{ referenciaEnvio }}</strong></p>
                  <p>{{ necesita.includes('PRECIO')
                    ? t('La notaría le enviará el presupuesto lo antes posible. Desde su página podrá verlo, aceptarlo y escribir a la notaría.')
                    : t('La notaría le responderá lo antes posible. Desde su página podrá ver su respuesta y escribir a la notaría.') }}</p>
                  <a v-if="enlaceClienteEnvio" :href="enlaceClienteEnvio" class="cg__boton cg__boton--secundario">{{ t('Ver mi consulta') }}</a>
                </template>
                <template v-else>
                  <h3>{{ t('Mensaje enviado') }}</h3>
                  <p>{{ t('La notaría le responderá lo antes posible.') }}</p>
                </template>
                <!-- Adelantar el encargo con el trámite y los datos de contacto ya puestos -->
                <SolicitudAdelantarTramite v-if="ofrecerAdelantar" :tramite="tramiteEnvio" :precarga="!!tramiteEnvio" />
                <p><button type="button" class="cg__enlace" @click="reiniciar">{{ t('Enviar otro mensaje') }}</button></p>
              </div>

              <!-- 1. Motivo -->
              <div v-else-if="!motivo">
                <p class="cg__pregunta">{{ t('¿En qué podemos ayudarle?') }}</p>
                <div class="cg__motivos" role="list">
                  <button v-for="m in MOTIVOS_CONTACTO" :key="m.codigo" type="button" class="cg__motivo" role="listitem"
                    @click="elegirMotivo(m.codigo)">
                    <v-icon size="26" class="cg__motivo-icono">{{ m.icono }}</v-icon>
                    <span class="cg__motivo-titulo">{{ t(m.titulo) }}</span>
                    <span class="cg__motivo-desc">{{ t(m.descripcion) }}</span>
                  </button>
                </div>
              </div>

              <!-- 2. Datos según el motivo -->
              <form v-else novalidate @submit.prevent="enviar">
                <button type="button" class="cg__volver" @click="motivo = null">
                  <v-icon size="18">mdi-arrow-left</v-icon> {{ t(motivoActual.titulo) }} · <u>{{ t('Cambiar') }}</u>
                </button>

                <!-- Trámite -->
                <template v-if="motivo === 'TRAMITE'">
                  <p class="cg__pregunta">{{ t('¿Qué trámite?') }}</p>
                  <div class="cg__chips">
                    <button v-for="tr in TRAMITES_CONTACTO" :key="tr.codigo" type="button" class="cg__chip"
                      :class="{ activo: tramite === tr.codigo }" :aria-pressed="tramite === tr.codigo" @click="elegirTramite(tr.codigo)">
                      <v-icon size="18">{{ tr.icono }}</v-icon> {{ t(tr.nombre) }}
                    </button>
                  </div>
                  <p v-if="errores.tramite" class="cg__error">{{ errores.tramite }}</p>

                  <template v-if="tramiteActual">
                    <!-- Hacerlo online -->
                    <div v-if="ofrecerOnline" class="cg__online">
                      <v-icon size="26">mdi-rocket-launch-outline</v-icon>
                      <div>
                        <strong>{{ t('Puede prepararlo online ahora') }}</strong>
                        <p>{{ t('Envíe sus datos y documentos en unos minutos y la notaría le contactará para prepararlo.') }}</p>
                        <NuxtLink :to="enlaceOnline" class="cg__boton cg__boton--secundario">{{ t('Empezar mi encargo online') }}</NuxtLink>
                      </div>
                    </div>
                    <p v-if="ofrecerOnline" class="cg__nota">{{ t('O, si prefiere hacer una consulta, siga aquí:') }}</p>

                    <!-- La documentación del trámite (la del encargo online): ya no hace falta preguntarla -->
                    <div v-if="documentosTramite.length" class="cg__docs">
                      <p class="cg__docs-titulo"><v-icon size="20">mdi-file-document-multiple-outline</v-icon> {{ t('Documentación que necesitará') }}</p>
                      <ul>
                        <li v-for="d in documentosTramite" :key="d.nombre">{{ d.nombre }}<span v-if="!d.obligatorio" class="cg__opcional"> ({{ t('si lo tiene') }})</span></li>
                      </ul>
                    </div>

                    <p class="cg__pregunta">{{ t('¿Qué necesita?') }} <span class="cg__opcional">{{ t('(puede marcar varias)') }}</span></p>
                    <div class="cg__chips">
                      <button v-for="n in NECESIDADES_TRAMITE" :key="n.codigo" type="button" class="cg__chip"
                        :class="{ activo: necesita.includes(n.codigo) }" :aria-pressed="necesita.includes(n.codigo)" @click="alternar(n.codigo)">
                        <v-icon v-if="necesita.includes(n.codigo)" size="16">mdi-check</v-icon> {{ t(n.texto) }}
                      </button>
                    </div>
                    <!-- Con cita de firma para el trámite: fecha preferida con las reglas del encargo (opcional). Ref: https://trello.com/c/63NGWLHt -->
                    <div v-if="necesita.includes('CITA') && reglasCitaGuiado" class="cg__cita">
                      <p class="cg__pregunta">{{ t('¿Cuándo le vendría bien firmar?') }} <span class="cg__opcional">{{ t('(opcional)') }}</span></p>
                      <CalendarioCita v-model="citaGuiado" :reglas="reglasCitaGuiado" id="cg-cita" :etiqueta="t('¿Cuándo le vendría bien firmar?')"
                        variable-acento="--cg-acento" />
                      <p class="cg__nota">{{ avisoCitaGuiado }}</p>
                    </div>
                  </template>
                </template>

                <!-- Copias, facturas y estado: primero la Notaría Virtual -->
                <div v-if="motivoActual.ofrecerNV && urlNV" class="cg__nv">
                  <v-icon size="24">mdi-account-lock-outline</v-icon>
                  <div>
                    <strong>{{ t('¿Tiene acceso a la Notaría Virtual?') }}</strong>
                    <p>{{ t('Allí puede ver sus escrituras, copias y facturas, y cómo va su asunto, sin esperar respuesta.') }}</p>
                    <a :href="urlNV" target="_blank" rel="noopener" class="cg__boton cg__boton--secundario">{{ t('Entrar en la Notaría Virtual') }}</a>
                    <p class="cg__nota mb-0">{{ t('Si no tiene acceso, rellene este formulario:') }}</p>
                  </div>
                </div>

                <!-- Otra consulta: los datos de la notaría por si basta -->
                <div v-if="motivo === 'OTRA' && (telefonoNotaria || emailNotaria)" class="cg__info">
                  <span v-if="telefonoNotaria"><v-icon size="18">mdi-phone</v-icon> <a :href="'tel:' + telefonoNotaria">{{ telefonoNotaria }}</a></span>
                  <span v-if="emailNotaria"><v-icon size="18">mdi-email-outline</v-icon> <a :href="'mailto:' + emailNotaria">{{ emailNotaria }}</a></span>
                </div>

                <!-- Preguntas del motivo o del trámite -->
                <div v-for="c in campos" :key="c.clave" class="cg__campo">
                  <label class="cg__etiqueta" :for="'cg-' + c.clave">{{ t(c.clave) }}<span v-if="c.obligatorio"> *</span></label>
                  <p v-if="c.ayuda" class="cg__ayuda">{{ t(c.ayuda) }}</p>

                  <template v-if="c.tipo === 'opciones'">
                    <div class="cg__chips">
                      <button v-for="o in c.opciones" :key="o" type="button" class="cg__chip" :class="{ activo: respuestas[c.clave] === o }"
                        :aria-pressed="respuestas[c.clave] === o" @click="respuestas[c.clave] = respuestas[c.clave] === o ? '' : o">{{ t(o) }}</button>
                    </div>
                    <p v-if="c.avisos?.[respuestas[c.clave]]" class="cg__ayuda cg__ayuda--aviso" role="status">
                      <v-icon size="16">mdi-information-outline</v-icon> {{ t(c.avisos[respuestas[c.clave]]) }}
                    </p>
                  </template>

                  <!-- Calendario propio (ref: https://trello.com/c/FlzpRILR): el selector nativo de type="date" no se abría
                       en todos los navegadores. La respuesta se guarda como aaaa-mm-dd (y " hh:mm" con hora) y se envía como dd/mm/aaaa.
                       "fecha" es un día pasado (fecha de la firma); "fechaFutura", un día desde hoy (fecha prevista, ref: https://trello.com/c/pYpZdaPv);
                       "fechaHora", un día desde hoy y la hora (cita, ref: https://trello.com/c/mm9XRZhS) -->
                  <v-menu v-else-if="c.tipo === 'fecha' || c.tipo === 'fechaFutura' || c.tipo === 'fechaHora'" v-model="calendario[c.clave]" :close-on-content-click="false" location="bottom start" min-width="0">
                    <template #activator="{ props: activador }">
                      <div class="cg__campo-fecha" v-bind="activador">
                        <input :id="'cg-' + c.clave" :value="formatearValor(c, respuestas[c.clave] || '')" type="text" readonly
                          class="cg__input cg__input--fecha" :placeholder="c.tipo === 'fechaHora' ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa'" autocomplete="off"
                          @keydown.enter.prevent="calendario[c.clave] = true" @keydown.space.prevent="calendario[c.clave] = true" />
                        <span class="cg__btn-calendario" aria-hidden="true"><v-icon size="20">mdi-calendar</v-icon></span>
                      </div>
                    </template>
                    <v-locale-provider :locale="localeVuetify" fallback-locale="en" :messages="mensajesVuetify">
                      <div class="cg__calendario">
                        <v-date-picker :model-value="fechaDeIso(respuestas[c.clave])" :color="acento" hide-header show-adjacent-months
                          :max="c.tipo === 'fecha' ? hoyIso : undefined" :min="c.tipo === 'fecha' ? '1900-01-01' : hoyIso"
                          @update:model-value="elegirFecha(c, $event)" />
                        <!-- Cita: después del día, la hora -->
                        <div v-if="c.tipo === 'fechaHora' && respuestas[c.clave]" class="cg__horas">
                          <p class="cg__horas-titulo">{{ t('Hora') }}</p>
                          <div class="cg__horas-lista">
                            <button v-for="h in HORAS_CITA" :key="h" type="button" class="cg__chip" :class="{ activo: horaDe(respuestas[c.clave]) === h }"
                              @click="elegirHora(c.clave, h)">{{ h }}</button>
                          </div>
                        </div>
                      </div>
                    </v-locale-provider>
                  </v-menu>

                  <input v-else :id="'cg-' + c.clave" v-model="respuestas[c.clave]" type="text" class="cg__input" maxlength="300"
                    :placeholder="c.ejemplo ? t(c.ejemplo) : ''" />

                  <p v-if="errores['c:' + c.clave]" class="cg__error">{{ errores['c:' + c.clave] }}</p>
                </div>

                <div class="cg__campo">
                  <label class="cg__etiqueta" for="cg-mensaje">
                    {{ motivo === 'OTRA' ? t('Su consulta') + ' *' : t('¿Algo más que debamos saber?') }}
                  </label>
                  <textarea id="cg-mensaje" v-model="mensaje" class="cg__input" rows="4" maxlength="3000"></textarea>
                  <p v-if="errores.mensaje" class="cg__error">{{ errores.mensaje }}</p>
                </div>

                <!-- Datos de contacto -->
                <p class="cg__pregunta mt-6">{{ t('Sus datos') }}</p>
                <div class="cg__rejilla">
                  <div class="cg__campo">
                    <label class="cg__etiqueta" for="cg-nombre">{{ t('Nombre') }} *</label>
                    <input id="cg-nombre" v-model="nombre" type="text" class="cg__input" autocomplete="name" maxlength="120" />
                    <p v-if="errores.nombre" class="cg__error">{{ errores.nombre }}</p>
                  </div>
                  <div class="cg__campo">
                    <label class="cg__etiqueta" for="cg-email">{{ t('Email') }} *</label>
                    <input id="cg-email" v-model="email" type="email" class="cg__input" autocomplete="email" maxlength="200" />
                    <p v-if="errores.email" class="cg__error">{{ errores.email }}</p>
                  </div>
                  <div class="cg__campo">
                    <label class="cg__etiqueta" for="cg-telefono">{{ t('Teléfono') }}<span v-if="pideTelefono"> *</span></label>
                    <input id="cg-telefono" v-model="telefono" type="tel" class="cg__input" autocomplete="tel" maxlength="30" />
                    <p v-if="errores.telefono" class="cg__error">{{ errores.telefono }}</p>
                  </div>
                </div>
                <p class="cg__etiqueta">{{ t('¿Cómo prefiere que le contactemos?') }} <span class="cg__opcional">({{ t('opcional') }})</span></p>
                <div class="cg__chips">
                  <button v-for="m in MEDIOS" :key="m.codigo" type="button" class="cg__chip" :class="{ activo: medios.includes(m.codigo) }"
                    :aria-pressed="medios.includes(m.codigo)" @click="alternarMedio(m.codigo)">
                    <v-icon size="16">{{ m.icono }}</v-icon> {{ t(m.texto) }}
                  </button>
                  <span class="cg__separador" />
                  <button v-for="f in FRANJAS" :key="f.codigo" type="button" class="cg__chip" :class="{ activo: franja === f.codigo }"
                    :aria-pressed="franja === f.codigo" @click="franja = franja === f.codigo ? '' : f.codigo">{{ t(f.texto) }}</button>
                </div>

                <!-- Honeypot -->
                <div class="cg__hp" aria-hidden="true"><label>Web <input v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off"></label></div>

                <label class="cg__consentimiento">
                  <input v-model="consentimiento" type="checkbox" />
                  <span>{{ t('He leído y acepto la') }} <NuxtLink to="/protecciondatos" target="_blank">{{ t('política de privacidad') }}</NuxtLink></span>
                </label>
                <p v-if="errores.consentimiento" class="cg__error">{{ errores.consentimiento }}</p>

                <div v-if="errorEnvio" class="cg__aviso" role="alert">
                  <v-icon size="20">mdi-alert-circle-outline</v-icon>
                  <span>{{ errorEnvio }}
                    <a v-if="emailNotaria && !errorEnvio.endsWith('.')" :href="'mailto:' + emailNotaria">{{ emailNotaria }}</a>
                  </span>
                </div>

                <button type="submit" class="cg__boton" :style="botonStyle" :disabled="enviando">
                  <v-progress-circular v-if="enviando" indeterminate size="18" width="2" class="mr-2" />
                  {{ t('Enviar a la notaría') }}
                </button>
              </form>
              <template #fallback><div class="cg__cargando" /></template>
            </ClientOnly>
          </div>
        </v-col>

        <v-col v-if="conImagen" lg="5" cols="12" class="d-none d-lg-block" :order="control.ImagenPosicion === 1 ? 1 : 2">
          <div class="cg__imagen"><img :src="imagenIDtoSRC(control.Imagen)" alt="" /></div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3'
import { useStore } from '../../store/index'
import { useUtils } from '#imports'
import { t, textosListos, cargarTextosSolicitud, idiomaSolicitud, IDIOMAS_RTL } from '../../composables/solicitudTextos'
import { useAccesoSolicitud } from '../../composables/solicitudAcceso'
import { apiSolicitudes } from '../../composables/useSolicitudWeb'
import { acentoLegible, textoLegibleSobre } from '../../composables/solicitudColor'
import { useEstiloFormulario } from '../../composables/estiloFormulario'
import { useIdiomaVuetify } from '../../composables/vuetifyIdioma'
import { MOTIVOS_CONTACTO, TRAMITES_CONTACTO, NECESIDADES_TRAMITE, type CodigoMotivo, type CampoContacto } from '../../data/contactoGuiado'
import { PRECARGA_ENCARGO, type PrecargaEncargo } from '../../composables/solicitudAcceso'
import type { TipoOperacionCatalogo, CitaPropuesta, ReglasCitaTramite } from '../../types/solicitudWeb'
import CalendarioCita from '../elements/CalendarioCita.vue'

const props = defineProps<{ contactForm: any }>()
const control = computed(() => props.contactForm || {})

const store = useStore()
const route = useRoute()
const { imagenIDtoSRC, getLocalePath } = useUtils()
const acceso = useAccesoSolicitud()

if (import.meta.client) cargarTextosSolicitud()

const MEDIOS = [
  { codigo: 'TELEFONO', texto: 'Teléfono', icono: 'mdi-phone-outline' },
  { codigo: 'EMAIL', texto: 'Email', icono: 'mdi-email-outline' },
  { codigo: 'WHATSAPP', texto: 'WhatsApp', icono: 'mdi-whatsapp' }
]
const FRANJAS = [
  { codigo: 'MANANA', texto: 'Por la mañana' },
  { codigo: 'TARDE', texto: 'Por la tarde' },
  { codigo: 'INDIFERENTE', texto: 'Me da igual' }
]

// --- Estado ---------------------------------------------------------------------------------
const motivo = ref<CodigoMotivo | null>(null)
const tramite = ref<string | null>(null)
const necesita = ref<string[]>([])
const respuestas = reactive<Record<string, string>>({})
const mensaje = ref('')
const nombre = ref('')
const email = ref('')
const telefono = ref('')
// Se pueden marcar varios modos de contacto (ref: https://trello.com/c/3Sgt9oHW); la franja sigue siendo una sola
const medios = ref<string[]>([])
const franja = ref('')
const consentimiento = ref(false)
const honeypot = ref('')
const errores = reactive<Record<string, string>>({})
const enviando = ref(false)
const enviado = ref(false)
const errorEnvio = ref('')
// Respuesta del envío de un trámite guardado como consulta (ref: https://trello.com/c/63NGWLHt)
const referenciaEnvio = ref('')
const enlaceClienteEnvio = ref('')
const tramiteEnvio = ref<string | null>(null)

const motivoActual = computed(() => MOTIVOS_CONTACTO.find((m) => m.codigo === motivo.value) || MOTIVOS_CONTACTO[0])
const tramiteActual = computed(() => TRAMITES_CONTACTO.find((x) => x.codigo === tramite.value) || null)
const campos = computed(() => motivo.value === 'TRAMITE' ? (tramiteActual.value?.campos ?? []) : motivoActual.value.campos)
const pideTelefono = computed(() => medios.value.includes('TELEFONO') || medios.value.includes('WHATSAPP'))
/** Medios en el orden de MEDIOS, separados por comas ("TELEFONO,WHATSAPP"), como los espera la API. */
const medio = computed(() => MEDIOS.map((m) => m.codigo).filter((c) => medios.value.includes(c)).join(','))
function alternarMedio(codigo: string) {
  medios.value = medios.value.includes(codigo) ? medios.value.filter((c) => c !== codigo) : [...medios.value, codigo]
}

// Encargo online (si la notaría lo tiene activo y el trámite está en su catálogo)
const ofrecerOnline = computed(() => !!tramiteActual.value?.online && acceso.activa.value)
const enlaceOnline = computed(() => acceso.enlace('contacto', tramiteActual.value?.codigo === 'OTRO' ? null : tramiteActual.value?.codigo))
// Después de enviar: adelantar el encargo (un trámite que se puede hacer online o una consulta general)
const ofrecerAdelantar = computed(() => acceso.activa.value && (motivo.value === 'OTRA' || ofrecerOnline.value))

// Documentación del trámite elegido: la del catálogo del encargo online de la notaría (traducido al idioma de la web)
const catalogoEncargo = ref<TipoOperacionCatalogo[] | null>(null)
let catalogoPedido = false
async function cargarCatalogoEncargo() {
  if (catalogoPedido || !acceso.activa.value) return
  catalogoPedido = true
  const n: any = store.NOTARIA
  try {
    const res = await $fetch<{ tipos: TipoOperacionCatalogo[]; config?: { Cita?: any } }>(
      `${apiSolicitudes(store)}api/SolicitudWeb/Catalogo/${n?.id || n?.IdValue}?idioma=${encodeURIComponent(idiomaSolicitud.value)}`,
      { retry: 1, timeout: 10000 })
    catalogoEncargo.value = res?.tipos ?? []
    configCita.value = res?.config?.Cita ?? null
  } catch { catalogoEncargo.value = [] /* sin lista: el formulario funciona igual */ }
}
// Cita de firma de la notaría (la misma del encargo online): reglas para el trámite elegido, o null si no la ofrece
const configCita = ref<any>(null)
const citaGuiado = ref<CitaPropuesta | null>(null)
const reglasCitaGuiado = computed<ReglasCitaTramite | null>(() => {
  const c = configCita.value
  const codigo = tramiteActual.value?.online ? tramiteActual.value.codigo : null
  const tr = codigo ? c?.tramites?.[codigo] : null
  if (!c || !tr) return null
  return { ...tr, diasSemana: c.diasSemana ?? [], diasCerrados: c.diasCerrados ?? [], horas: c.horas ?? [] }
})
const avisoCitaGuiado = computed(() => configCita.value?.texto ||
  t('La fecha y la hora son orientativas. La notaría se pondrá en contacto con usted para confirmarlas.'))
const documentosTramite = computed(() => {
  const codigo = tramiteActual.value?.online ? tramiteActual.value.codigo : null
  return (codigo && catalogoEncargo.value?.find((x) => x.codigo === codigo)?.documentos) || []
})

// Notaría Virtual
const urlNV = computed(() => {
  const n: any = store.NOTARIA
  if (!n?.NotariaVirtual) return ''
  if (n.NotariaVirtualFrame) return getLocalePath('/NotariaVirtual')
  return /^https?:\/\//i.test(n.NotariaVirtual) ? n.NotariaVirtual : 'https://' + n.NotariaVirtual
})
const telefonoNotaria = computed(() => (store.NOTARIA as any)?.Contacto?.Telefono || '')
const emailNotaria = computed(() => (store.NOTARIA as any)?.Contacto?.Email || '')

// --- Estilo (los colores del panel, como los otros dos diseños) -------------------------------
// En las plantillas 2 a 5 manda la plantilla: su acento, sus fuentes y sus colores (ref: https://trello.com/c/LccFAOAR)
const { enPlantilla, estilo, acentoPlantilla } = useEstiloFormulario(() => control.value)
const acento = computed(() => enPlantilla.value ? acentoPlantilla.value : (control.value.Button?.css?.BackgroundColor || '#1f3a5f'))
const acentoTexto = computed(() => textoLegibleSobre(acento.value, enPlantilla.value ? undefined : control.value.Button?.css?.Color))
// Enlaces sobre la tarjeta blanca: el acento si se lee sobre blanco; si no, el por defecto
const enlace = computed(() => acentoLegible(acento.value))
const colorTexto = computed(() => enPlantilla.value ? 'var(--n2-texto)' : (control.value.Titulo?.css?.Color || '#1d2329'))
const conImagen = computed(() => !!control.value.Imagen)
const tituloStyle = computed(() => ({
  color: estilo.value.titulo.color, fontFamily: estilo.value.titulo.fuente,
  fontSize: estilo.value.titulo.tamano, fontWeight: estilo.value.titulo.peso
}))
const descripcionStyle = computed(() => ({
  color: estilo.value.descripcion.color, fontFamily: estilo.value.descripcion.fuente,
  fontSize: estilo.value.descripcion.tamano
}))
const botonStyle = computed(() => ({
  backgroundColor: acento.value, color: acentoTexto.value,
  fontFamily: estilo.value.boton.fuente
}))

// --- Acciones -----------------------------------------------------------------------------------
function limpiarErrores() { Object.keys(errores).forEach((k) => delete errores[k]) }

function elegirMotivo(m: CodigoMotivo) {
  motivo.value = m
  limpiarErrores()
  errorEnvio.value = ''
}

function elegirTramite(c: string) {
  tramite.value = c
  delete errores.tramite
  cargarCatalogoEncargo()
}

function alternar(c: string) {
  necesita.value = necesita.value.includes(c) ? necesita.value.filter((x) => x !== c) : [...necesita.value, c]
}

function reiniciar() {
  enviado.value = false
  referenciaEnvio.value = ''
  enlaceClienteEnvio.value = ''
  tramiteEnvio.value = null
  motivo.value = null
  tramite.value = null
  necesita.value = []
  citaGuiado.value = null
  Object.keys(respuestas).forEach((k) => delete respuestas[k])
  mensaje.value = ''
}

function validar() {
  limpiarErrores()
  if (motivo.value === 'TRAMITE' && !tramite.value) errores.tramite = t('Elija un trámite')
  for (const c of campos.value)
    if (c.obligatorio && !(respuestas[c.clave] || '').trim()) errores['c:' + c.clave] = t('Este dato es obligatorio')
  if (motivo.value === 'OTRA' && !mensaje.value.trim()) errores.mensaje = t('Escriba su consulta')
  if (!nombre.value.trim()) errores.nombre = t('Este dato es obligatorio')
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim())) errores.email = t('El email no es válido')
  if (pideTelefono.value && telefono.value.replace(/\D/g, '').length < 8) errores.telefono = t('El teléfono no es válido')
  else if (telefono.value.trim() && telefono.value.replace(/\D/g, '').length < 8) errores.telefono = t('El teléfono no es válido')
  if (!consentimiento.value) errores.consentimiento = t('Debe aceptar la política de privacidad para enviar')
  return Object.keys(errores).length === 0
}

// reCAPTCHA v3 (mismo grupo de claves que el formulario de contacto actual)
let obtenerToken: (() => Promise<string>) | null = null
if (import.meta.client && store.recaptchaSiteKey) {
  const { vueApp } = useNuxtApp()
  if (!(vueApp.config.globalProperties as any).$recaptcha)
    vueApp.use(VueReCaptcha, { siteKey: store.recaptchaSiteKey, loaderOptions: { autoHideBadge: false } })
  const rc = useReCaptcha()
  if (rc) obtenerToken = async () => { await rc.recaptchaLoaded(); return rc.executeRecaptcha('email') }
}

const escHtml = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Fecha local (toISOString da la de UTC: entre las 00:00 y las 02:00 en España sería ayer)
const hoyIso = (() => { const h = new Date(); return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}` })()

// Calendario de los campos de fecha: abierto o cerrado por campo, con los textos de Vuetify en el idioma de la web
const { locale: localeVuetify, messages: mensajesVuetify } = useIdiomaVuetify()
const calendario = reactive<Record<string, boolean>>({})
const isoLocal = (f: Date) => `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`
function fechaDeIso(valor?: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor || '')
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}
// Horas de la cita: de 8:00 a 20:30, cada media hora
const HORAS_CITA = Array.from({ length: 26 }, (_, i) => `${String(8 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`)
const horaDe = (valor?: string) => /\d{2}:\d{2}$/.exec(valor || '')?.[0] ?? ''
function elegirFecha(campo: CampoContacto, valor: unknown) {
  const f = Array.isArray(valor) ? valor[0] : valor
  if (!(f instanceof Date) || isNaN(f.getTime())) return
  const hora = horaDe(respuestas[campo.clave])
  respuestas[campo.clave] = isoLocal(f) + (campo.tipo === 'fechaHora' && hora ? ' ' + hora : '')
  // Las fechas sin hora se cierran al elegir el día; la cita, al elegir la hora
  if (campo.tipo !== 'fechaHora' || hora) calendario[campo.clave] = false
}
function elegirHora(clave: string, hora: string) {
  respuestas[clave] = (respuestas[clave] || '').slice(0, 10) + ' ' + hora
  calendario[clave] = false
}

function formatearValor(campo: CampoContacto, valor: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}:\d{2}))?$/.exec(valor)
  if ((campo.tipo === 'fecha' || campo.tipo === 'fechaFutura' || campo.tipo === 'fechaHora') && m) return `${m[3]}/${m[2]}/${m[1]}` + (m[4] ? ' ' + m[4] : '')
  return valor
}

/** Alternativa de envío: mailing + copia en la bandeja de mensajes del panel. Devuelve true si el correo salió. */
async function enviarPorMailing(token: string): Promise<boolean> {
  const n: any = store.NOTARIA
  const destino = n?.Contacto?.Email
  if (!destino) return false
  const nombreMotivo = motivoActual.value?.titulo || ''
  const nombreTramite = tramiteActual.value?.nombre || ''
  const origen = route.path === '/' || /^\/[a-z]{2}\/?$/.test(route.path) ? 'FORM_INICIO' : 'FORM_CONTACTO'
  const lineas: string[] = []
  if (nombreMotivo) lineas.push(`Motivo: ${escHtml(nombreMotivo)}`)
  if (nombreTramite) lineas.push(`Trámite: ${escHtml(nombreTramite)}`)
  if (necesita.value.length) lineas.push(`Necesita: ${escHtml(necesita.value.join(', '))}`)
  campos.value.filter((c) => (respuestas[c.clave] || '').trim()).forEach((c) => lineas.push(`${escHtml(c.clave)}: ${escHtml(formatearValor(c, respuestas[c.clave].trim()))}`))
  if (mensaje.value.trim()) lineas.push(`Mensaje: ${escHtml(mensaje.value.trim()).replace(/\n/g, '<br>')}`)
  if (medio.value || franja.value) lineas.push(`Contacto preferido: ${escHtml([medio.value, franja.value].filter(Boolean).join(' · '))}`)
  const cuerpo = `
    Mensaje enviado desde la web: ${escHtml(location.origin)}<br><br>
    <b>ATENCIÓN:</b> Haga click sobre la dirección de correo de contacto para responder al remitente.<br><br>
    Remitente: ${escHtml(nombre.value.trim())}<br><br>
    Correo de contacto: <a href="mailto:${escHtml(email.value.trim())}">${escHtml(email.value.trim())}</a><br><br>
    ${telefono.value.trim() ? `Teléfono: ${escHtml(telefono.value.trim())}<br><br>` : ''}
    ${lineas.join('<br><br>')}
    <br><br><b>Por favor, no responda a este correo. Este mensaje ha sido generado automáticamente.</b>
  `
  const textoPlano = lineas.join('\n').replace(/<br>/g, '\n').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&amp;/g, '&')
  const asunto = `Contacto WEB - ${n?.Nombre || ''} - ${nombre.value.trim()} - ${nombreMotivo || 'Consulta'} - ${origen}`

  // Copia en la bandeja del panel (no bloquea el envío)
  try {
    const modelo = { Subject: asunto, Name: nombre.value.trim(), Email: email.value.trim(), Phone: telefono.value.trim(), Message: textoPlano }
    const apiBase = (store.backend_api_url || (process.env.NODE_ENV === 'development' ? store.api_url : 'https://controlpanel.notin.es/')).replace(/\/+$/, '')
    const bodystr = btoa(unescape(encodeURIComponent(JSON.stringify(modelo))))
    $fetch(`${apiBase}/api/Emails/Public/${n?.id || n?.IdValue}`, { method: 'POST', body: { bodystr } }).catch((e) => console.warn('No se pudo registrar copia del formulario en el panel:', e))
  } catch { /* la copia en el panel es opcional */ }

  try {
    await $fetch('https://mailing.notin.es/api/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        perfil: 'info_recaptcha_web_notaria',
        cuerpo,
        is_cuerpo_html: true,
        asunto,
        destinatarios: [{ nombre: n?.Nombre, direccion: destino }],
        destinatarios_cco: [
          { nombre: 'webNotin', direccion: 'web@notin.net' },
          { nombre: 'avisosUnidata', direccion: 'pwavisos@unidata.es' }
        ],
        recaptcha: { id_credential: n?.Personalizar?.RecaptchaGroup ?? null, token, action: 'email' },
        data: { id_notaria: n?.id, Fecha: new Date().toISOString(), Name: nombre.value.trim(), Email: email.value.trim(), Phone: telefono.value.trim(), Subject: asunto, Message: textoPlano }
      }
    })
    return true
  } catch (e) {
    console.error('Error de envío por mailing del formulario guiado', e)
    return false
  }
}

/** Deja el trámite y los datos de contacto para el encargo online, por si quiere adelantarlo (ver PRECARGA_ENCARGO). */
function guardarPrecarga() {
  const codigo = ofrecerOnline.value && tramiteActual.value?.codigo !== 'OTRO' ? tramiteActual.value?.codigo ?? null : null
  tramiteEnvio.value = codigo
  if (!codigo) return
  try {
    const p: PrecargaEncargo = {
      tramite: codigo, nombre: nombre.value.trim(), email: email.value.trim(), telefono: telefono.value.trim(),
      presupuesto: necesita.value.includes('PRECIO'), fecha: Date.now()
    }
    sessionStorage.setItem(PRECARGA_ENCARGO, JSON.stringify(p))
  } catch { /* sin almacenamiento: el encargo empieza con el trámite elegido */ }
}

async function enviar() {
  errorEnvio.value = ''
  if (!validar()) {
    errorEnvio.value = t('Revise los datos marcados.')
    return
  }
  enviando.value = true
  let token = ''
  try {
    try { token = obtenerToken ? await obtenerToken() : '' } catch { token = '' }
    const n: any = store.NOTARIA
    const res = await $fetch<{ ok: boolean; referencia?: string; enlaceCliente?: string }>(`${apiSolicitudes(store)}api/ContactoWeb/Public/${n?.id || n?.IdValue}`, {
      method: 'POST',
      body: {
        motivo: motivo.value,
        tramite: tramite.value,
        tramiteNombre: tramiteActual.value?.nombre ?? null,
        necesita: necesita.value,
        cita: necesita.value.includes('CITA') && reglasCitaGuiado.value && citaGuiado.value?.fecha ? citaGuiado.value : null,
        respuestas: campos.value
          .filter((c) => (respuestas[c.clave] || '').trim())
          .map((c) => ({ pregunta: c.clave, respuesta: formatearValor(c, respuestas[c.clave].trim()) })),
        mensaje: mensaje.value.trim(),
        nombre: nombre.value.trim(),
        email: email.value.trim(),
        telefono: telefono.value.trim(),
        contactoPreferido: medio.value || franja.value ? { medio: medio.value, franja: franja.value } : null,
        idioma: idiomaSolicitud.value,
        origen: route.path === '/' || /^\/[a-z]{2}\/?$/.test(route.path) ? 'FORM_INICIO' : 'FORM_CONTACTO',
        consentimiento: consentimiento.value,
        website: honeypot.value,
        recaptcha: { id_credential: n?.Personalizar?.RecaptchaGroup ?? null, token, action: 'email' }
      }
    })
    referenciaEnvio.value = res?.referencia || ''
    enlaceClienteEnvio.value = res?.enlaceCliente || ''
    guardarPrecarga()
    enviado.value = true
  } catch (e: any) {
    const estado = e?.response?.status
    // Si el servicio de contacto no responde (redirección al login, red, 5xx...) se envía por mailing como el resto de formularios.
    // Con 400/429 la petición se ha rechazado a propósito (datos no válidos, demasiados envíos, bot): no se reenvía.
    if (estado !== 429 && estado !== 400 && !honeypot.value && await enviarPorMailing(token)) {
      guardarPrecarga()
      enviado.value = true
      enviando.value = false
      return
    }
    errorEnvio.value = estado === 429
      ? t('Ha hecho demasiados envíos seguidos. Espere unos minutos y vuelva a intentarlo.')
      : emailNotaria.value ? t('No hemos podido enviar el mensaje. Inténtelo de nuevo o escríbanos a')
        : t('No hemos podido enviar el mensaje. Inténtelo de nuevo en unos minutos.')
  } finally {
    enviando.value = false
  }
}

onMounted(() => {
  // Enlace directo: /contacto?motivo=COPIA
  const m = String(route.query.motivo || '').toUpperCase()
  if (MOTIVOS_CONTACTO.some((x) => x.codigo === m)) motivo.value = m as CodigoMotivo
})
</script>

<style lang="scss" scoped>
.cg {
  padding: 64px 0;
  color: var(--cg-texto);
  &__fila { align-items: stretch; }
  &__tarjeta {
    background: #fff; border: 1px solid #e3e7eb; border-radius: 20px; padding: 40px;
    box-shadow: 0 16px 40px rgba(20, 30, 40, .06); height: 100%;
    @media (max-width: 600px) { padding: 22px 16px; border-radius: 14px; }
  }
  &__cabecera { margin-bottom: 24px; }
  &__titulo { margin: 0 0 8px; line-height: 1.2; }
  &__descripcion { margin: 0; opacity: .85; }
  &__cargando { height: 260px; border-radius: 12px; background: linear-gradient(90deg, #f1f3f5 25%, #f7f8f9 50%, #f1f3f5 75%); background-size: 200% 100%; animation: cg-brillo 1.4s infinite linear; }
  &__pregunta { font-weight: 700; font-size: 17.5px; margin: 0 0 12px; color: #1d2329; }
  &__opcional { font-weight: 400; font-size: 13.5px; color: #6b7680; }
  &__motivos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
    @media (max-width: 960px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 520px) { grid-template-columns: 1fr; } }
  &__motivo {
    display: flex; flex-direction: column; align-items: flex-start; gap: 4px; text-align: start;
    padding: 16px; border: 2px solid #e3e7eb; border-radius: 14px; background: #fff; cursor: pointer;
    transition: border-color .15s, box-shadow .15s, transform .15s; min-height: 110px;
    &:hover, &:focus-visible { border-color: var(--cg-acento); box-shadow: 0 6px 18px rgba(20, 30, 40, .08); transform: translateY(-1px); outline: none; }
  }
  &__motivo-icono { color: var(--cg-acento) !important; margin-bottom: 6px; }
  &__motivo-titulo { font-weight: 700; color: #1d2329; line-height: 1.25; }
  &__motivo-desc { font-size: 14px; color: #5f6b76; line-height: 1.35; }
  &__volver { display: inline-flex; align-items: center; gap: 6px; background: none; border: 0; padding: 0; margin-bottom: 18px; color: #5f6b76; cursor: pointer; font-size: 15px; }
  &__chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  &__chip {
    display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; border-radius: 999px;
    border: 1.5px solid #cfd6dc; background: #fff; color: #1d2329; cursor: pointer; font-size: 15px; min-height: 40px;
    &.activo { border-color: var(--cg-acento); background: var(--cg-acento); color: #fff; }
    &:focus-visible { outline: 3px solid color-mix(in srgb, var(--cg-acento) 40%, transparent); outline-offset: 2px; }
  }
  &__separador { width: 1px; background: #e3e7eb; margin: 0 4px; }
  &__campo { margin-bottom: 16px; }
  &__etiqueta { display: block; font-weight: 600; margin-bottom: 6px; color: #1d2329; }
  &__ayuda { font-size: 14px; color: #5f6b76; margin: -2px 0 8px; }
  &__ayuda--aviso { margin: 4px 0 0; padding: 8px 12px; border-radius: 10px; background: #fff8e1; color: #5d4500; }
  &__input {
    width: 100%; padding: 12px 14px; border: 1.5px solid #cfd6dc; border-radius: 10px; font-size: 16px; background: #fff; color: #1d2329;
    &:focus { border-color: var(--cg-acento); outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--cg-acento) 18%, transparent); }
  }
  &__campo-fecha {
    position: relative;
    display: flex;
    align-items: center;
  }
  &__input--fecha {
    padding-right: 42px;
    cursor: pointer;
    font-family: inherit;
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: auto;
      color: transparent;
      background: transparent;
      cursor: pointer;
    }
  }
  &__calendario { background: #fff; color: #1d2329; border: 1px solid #e3e7eb; border-radius: 14px; box-shadow: 0 16px 40px rgba(20, 30, 40, .16); max-width: calc(100vw - 32px); overflow: hidden;
    .v-date-picker { background: transparent; } }
  &__horas { padding: 0 16px 16px; max-width: 328px; }
  &__horas-titulo { margin: 0 0 8px; font-weight: 700; font-size: 15px; }
  &__horas-lista { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; max-height: 164px; overflow-y: auto;
    .cg__chip { justify-content: center; padding: 6px 4px; min-height: 34px; font-size: 14px; } }
  &__btn-calendario {
    position: absolute;
    right: 12px;
    background: none;
    border: 0;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--cg-acento);
    cursor: pointer;
    pointer-events: none;
    .v-icon { color: inherit !important; }
  }
  textarea.cg__input { resize: vertical; }
  &__rejilla { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 12px; @media (max-width: 760px) { grid-template-columns: 1fr; } }
  &__error { color: #b3261e; font-size: 14px; margin: 6px 0 0; }
  &__nota { color: #5f6b76; margin: 10px 0 14px; }
  &__online, &__nv {
    display: flex; gap: 14px; align-items: flex-start; border-radius: 14px; padding: 16px 18px; margin: 14px 0 6px;
    p { margin: 4px 0 12px; color: #3d4852; }
  }
  &__online { background: #eef6ff; border: 1px solid #cfe3fb; .v-icon { color: #1565c0 !important; } }
  &__nv { background: #f1f8f2; border: 1px solid #cfe6d3; margin-bottom: 18px; .v-icon { color: #2e7d32 !important; } }
  &__info { display: flex; flex-wrap: wrap; gap: 8px 22px; background: #f4f6f8; border-radius: 12px; padding: 12px 16px; margin-bottom: 18px; color: #1d2329; a { color: inherit !important; } }
  &__boton {
    display: inline-flex; align-items: center; justify-content: center; padding: 13px 28px; border: 0; border-radius: 999px;
    background: var(--cg-acento); color: #fff; font-weight: 700; font-size: 16px; cursor: pointer; text-decoration: none; min-height: 48px;
    &:disabled { opacity: .7; cursor: default; }
    &--secundario { padding: 10px 20px; min-height: 42px; font-size: 15px; }
    // Los botones-enlace (encargo online, Notaría Virtual) usan el texto legible del botón del panel. El CSS de cada notaría
    // pinta los <a> con el color del tema, que puede coincidir con el fondo y dejar el botón "sin texto"
    &--secundario, &--secundario:link, &--secundario:visited, &--secundario:hover, &--secundario:focus { color: var(--cg-acento-texto) !important; }
  }
  &__enlace { background: none; border: 0; color: var(--cg-acento); text-decoration: underline; cursor: pointer; }
  // El CSS de cada notaría puede pintar los <a> (y el texto) en blanco para fondos oscuros; la tarjeta siempre es blanca
  &__consentimiento { display: flex; gap: 10px; align-items: flex-start; margin: 18px 0 6px; cursor: pointer; color: #1d2329;
    a, a:visited, a:hover { color: var(--cg-enlace) !important; text-decoration: underline; }
    input { width: 20px; height: 20px; margin-top: 2px; accent-color: var(--cg-acento); } }
  &__aviso { display: flex; gap: 8px; align-items: flex-start; background: #fdecea; color: #7a1c14; border-radius: 10px; padding: 12px 14px; margin: 14px 0; a { color: inherit !important; } }
  &__ok { text-align: center; padding: 30px 10px; h3 { margin: 10px 0 6px; } p { color: #5f6b76; } }
  &__referencia { strong { color: #1d2329; letter-spacing: .02em; } }
  &__docs { background: #f7f8fa; border: 1px solid #e3e7eb; border-radius: 12px; padding: 12px 16px; margin: 6px 0 18px; color: #1d2329;
    ul { margin: 6px 0 0; padding-inline-start: 22px; } li { margin: 2px 0; }
    .v-icon { color: var(--cg-acento) !important; } }
  &__docs-titulo { display: flex; align-items: center; gap: 8px; font-weight: 700; margin: 0; }
  &__hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }
  // La ilustración no se estira con el formulario: altura fija, arriba, como en el diseño Moderno
  &__imagen { height: 520px; max-height: 70vh; border-radius: 20px; overflow: hidden; img { width: 100%; height: 100%; object-fit: cover; } }
  // El tema de las webs fuerza un tamaño de letra en todos los <p> con más especificidad que una sola clase
  & &__ayuda, & &__error { font-size: 14px; }
  & &__pregunta { font-size: 17px; }
}
@keyframes cg-brillo { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
</style>
